import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";

const LAST_UPDATED = "20 February 2026";
const SUPPORT_EMAIL = "support@skillpreneuracademy.com";

const POLICIES = {
  "terms-and-conditions": {
    title: "Terms & Conditions",
    seoTitle: "Terms & Conditions | SkillPreneur Academy",
    seoDescription:
      "Read the Terms & Conditions governing the use of SkillPreneur Academy courses, mentorship, community access and digital products.",
    canonical: "/terms-and-conditions",
    intro:
      "These Terms govern your access to and use of the SkillPreneur Academy website, courses, mentorship programs, workshops, recordings, downloadable resources and community. By accessing or purchasing any service from SkillPreneur Academy, you agree to be bound by these Terms.",
    sections: [
      {
        h: "1. Acceptance of Terms",
        p: "By accessing, browsing, registering, enrolling, or making a purchase on SkillPreneur Academy, you confirm that you have read, understood and agree to be bound by these Terms & Conditions, our Privacy Policy and our Refund & Cancellation Policy. If you do not agree, please do not use the platform.",
      },
      {
        h: "2. Personal Educational Use Only",
        p: "All courses, mentorship sessions, workshops, recordings, templates, downloadable resources, bonuses and community access provided by SkillPreneur Academy are licensed solely for your personal educational use.",
        list: [
          "Access is personal and tied to a single user / single enrolment.",
          "You may not use the content for commercial training, reselling, or running a competing program.",
        ],
      },
      {
        h: "3. Intellectual Property & Content Protection",
        p: "All content on the platform — including but not limited to video lessons, audio recordings, PDFs, worksheets, templates, scripts, presentations, course curriculum, branding, logos and copy — is the exclusive intellectual property of SkillPreneur Academy.",
        list: [
          "Content may NOT be copied, resold, redistributed, shared, recorded, screen-captured, reproduced or transferred in any form without prior written permission.",
          "Uploading any course material to YouTube, Telegram, Drive folders, torrent sites or any public/private channel is strictly prohibited and will be treated as piracy.",
          "Violation may result in immediate suspension of access and legal action under applicable copyright laws.",
        ],
      },
      {
        h: "4. Account Security & No Account Sharing",
        p: "You are responsible for maintaining the confidentiality of your account credentials. Account sharing is strictly prohibited — each enrolment grants access to a single user only. We monitor for unusual access patterns and reserve the right to suspend any account suspected of being shared.",
      },
      {
        h: "5. Suspension & Termination",
        p: "SkillPreneur Academy reserves the right to suspend or terminate access — without refund — in case of:",
        list: [
          "Misuse, abuse or harassment of mentors, students or staff.",
          "Fraudulent payment, identity fraud or chargeback abuse.",
          "Piracy, leaking, or unauthorized redistribution of course content.",
          "Violation of community guidelines or these Terms.",
        ],
      },
      {
        h: "6. No Guarantee of Results or Earnings",
        p: "SkillPreneur Academy provides educational content, frameworks, mentorship and resources. We do NOT guarantee any specific income, earnings, freelance projects, job placement, follower count or business outcome.",
        list: [
          "Results depend entirely on individual effort, skill, consistency, market conditions and implementation.",
          "Any case studies, screenshots, testimonials or earning examples shown on the website are individual results and not promises.",
          "You are solely responsible for your own decisions, actions and financial commitments.",
        ],
      },
      {
        h: "7. Changes to Content, Pricing & Policies",
        p: "We continuously improve our programs. SkillPreneur Academy may update, add to, modify or discontinue course content, modules, mentors, bonuses, resources, pricing or any of these policies at any time without prior notice. The latest version of every policy will always be available on this website.",
      },
      {
        h: "8. Third-Party Services",
        p: "We integrate with trusted third-party providers (e.g., Razorpay for payments, Resend for transactional emails). Your use of those services is also governed by their respective terms and privacy policies.",
      },
      {
        h: "9. Limitation of Liability",
        p: "To the maximum extent permitted by applicable law, SkillPreneur Academy, its founders, employees and partners shall not be liable for any indirect, incidental, consequential or special damages arising out of or related to your use of the platform.",
      },
      {
        h: "10. Governing Law & Jurisdiction",
        p: "These Terms are governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the competent courts of Mumbai, Maharashtra.",
      },
      {
        h: "11. Contact",
        p: `For any questions related to these Terms, please email ${SUPPORT_EMAIL}.`,
      },
    ],
  },

  "privacy-policy": {
    title: "Privacy Policy",
    seoTitle: "Privacy Policy | SkillPreneur Academy",
    seoDescription:
      "Learn what data SkillPreneur Academy collects, how we use it, and how we protect your personal information.",
    canonical: "/privacy-policy",
    intro:
      "SkillPreneur Academy values and respects your privacy. This Privacy Policy explains what information we collect, how we use it, how we share it and how we protect it when you visit our website, sign up for a free consultation or enroll in any of our programs.",
    sections: [
      {
        h: "1. Information We Collect",
        p: "We collect only the information needed to deliver our services and improve your learning experience:",
        list: [
          "Name",
          "Email address",
          "Phone number",
          "Payment information (processed securely by our payment partner — never stored on our servers)",
          "Order details (package purchased, courses selected, coupon used, GST receipt)",
          "Website analytics (pages visited, traffic source, session behaviour)",
          "Device information (browser type, operating system, IP address, user-agent)",
          "Legal consent records (timestamp, IP and user-agent at the moment of checkout)",
        ],
      },
      {
        h: "2. How We Use Your Information",
        p: "Your information is used strictly to operate and improve SkillPreneur Academy:",
        list: [
          "Granting and managing access to the course(s) you purchased.",
          "Providing student support, mentorship coordination and onboarding.",
          "Communication — transactional emails, payment confirmations, schedule updates and important announcements.",
          "Processing payments securely through our payment provider.",
          "Maintaining platform security, fraud prevention and chargeback dispute records.",
          "Improving our services, course content, website and user experience through anonymized analytics.",
        ],
      },
      {
        h: "3. Payment Information",
        p: "Payments on SkillPreneur Academy are processed securely through trusted third-party payment providers (such as Razorpay). Card numbers, CVVs, UPI PINs and bank credentials are NEVER stored on our servers. Only the transaction reference, amount and status are stored for reconciliation and tax compliance.",
      },
      {
        h: "4. We Do Not Sell Your Data",
        p: "SkillPreneur Academy does not sell, rent or trade your personal information to third parties for marketing purposes. Ever.",
      },
      {
        h: "5. Sharing With Trusted Service Providers",
        p: "We share information only with trusted service providers strictly necessary to operate the platform. These include:",
        list: [
          "Payment processors (e.g., Razorpay) — to charge and verify payments.",
          "Transactional email providers (e.g., Resend) — to deliver receipts, course access emails and support replies.",
          "Cloud hosting and analytics providers — to keep the platform fast, secure and reliable.",
          "Our internal mentors and support staff — strictly on a need-to-know basis to deliver the program you enrolled in.",
        ],
      },
      {
        h: "6. Data Security",
        p: "We take reasonable technical and organizational measures to protect your data, including HTTPS encryption in transit, hashed passwords, secure httpOnly session cookies, restricted admin access, and regular security review of all third-party integrations.",
      },
      {
        h: "7. Cookies & Local Storage",
        p: "We use cookies, localStorage and sessionStorage to keep you logged in, remember your cart, theme preference and applied coupon codes. We do not run third-party advertising trackers.",
      },
      {
        h: "8. Your Rights",
        p: `You may contact us at any time to request a copy of, correction of, or deletion of your personal data. Email ${SUPPORT_EMAIL} from the registered email address and we will respond within 7 working days, subject to applicable legal retention requirements.`,
      },
      {
        h: "9. Data Retention",
        p: "Order and payment records are retained for the duration required by applicable tax and accounting law. Marketing/enquiry leads are retained until you request deletion. Course-access records are retained for as long as you hold lifetime access.",
      },
      {
        h: "10. Changes to This Policy",
        p: "We may update this Privacy Policy from time to time to reflect changes in our services or applicable law. The latest version will always be available on this page with the 'Last updated' date refreshed.",
      },
      {
        h: "11. Contact Us",
        p: `For privacy-related questions or data requests, write to ${SUPPORT_EMAIL}.`,
      },
    ],
  },

  "refund-policy": {
    title: "Refund & Cancellation Policy",
    seoTitle: "Refund & Cancellation Policy | SkillPreneur Academy",
    seoDescription:
      "SkillPreneur Academy offers a 2-hour refund window on all digital purchases. Read the full refund and cancellation terms.",
    canonical: "/refund-policy",
    intro:
      "We want you to be completely satisfied with your purchase. Please read this Refund & Cancellation Policy carefully before completing your purchase on SkillPreneur Academy. By completing payment, you acknowledge and accept the terms below.",
    callout:
      "REFUND WINDOW: 2 HOURS. If you are not satisfied with your purchase, you can request a full refund within 2 hours of payment. After 2 hours, all sales are final.",
    sections: [
      {
        h: "1. Nature of Our Products",
        p: "SkillPreneur Academy primarily provides digital products and digital educational services, including:",
        list: [
          "Online video courses (E-Lite, Basic & Premium packages, individual courses)",
          "Live and recorded training programs",
          "1-on-1 and group mentorship sessions",
          "Downloadable resources, templates and worksheets",
          "Recordings of live sessions",
          "Access to private community groups and mentorship channels",
        ],
      },
      {
        h: "2. Refund Window — 2 Hours",
        p: "You can request a full refund within 2 hours of your payment. After the 2-hour window expires, all sales are final.",
        list: [
          "This is a digital product with immediate access after payment.",
          "Refunds are ONLY available within 2 hours of purchase.",
          "No refunds are issued after the 2-hour window expires.",
          "Both QR payments and website purchases are subject to this policy.",
          "Enrolments cannot be exchanged for a different package or transferred to another person.",
          "Coupons, discounts and promotional credits are non-refundable and non-transferable.",
        ],
      },
      {
        h: "3. How to Request a Refund",
        p: "Refund requests are processed within 24 hours of receipt. To request one:",
        list: [
          `Email ${SUPPORT_EMAIL} within 2 hours of your payment.`,
          "Include your Student ID and the email address used at checkout.",
          "Provide the reason for your refund request.",
          "Approved refunds are returned to the original payment method.",
        ],
      },
      {
        h: "3. Review Before You Buy",
        p: "We strongly encourage every prospective student to review all program details, curriculum, mentor profiles, success stories, FAQs and any free resources before completing payment. If you are unsure, please reach out to our team to book a free consultation prior to enrolling.",
      },
      {
        h: "4. Acceptance of Policy at Checkout",
        p: "Every successful checkout at SkillPreneur Academy requires acceptance of this Refund & Cancellation Policy via a mandatory consent checkbox. By completing payment, you confirm that you have read, understood and accepted this no-refund policy. We capture this consent along with the timestamp, IP address and user-agent for legal compliance.",
      },
      {
        h: "5. Chargeback Abuse",
        p: "Initiating a chargeback or payment dispute without first contacting our support team is considered a violation of these Terms.",
        list: [
          "Fraudulent disputes, false chargebacks or misuse of the payment system will result in immediate suspension of access.",
          "We may pursue further action — including legal remedies — where applicable.",
          "Please always contact " + SUPPORT_EMAIL + " first; we resolve genuine concerns promptly.",
        ],
      },
      {
        h: "6. Duplicate Payments",
        p: "If a duplicate payment is captured due to a technical error (e.g., the same order is charged twice within minutes), the duplicate transaction may be reviewed and refunded after verification. To request a duplicate-payment review:",
        list: [
          `Email ${SUPPORT_EMAIL} within 7 days of the transaction.`,
          "Include both transaction IDs, the payment date and a screenshot of the bank statement.",
          "Approved duplicates are refunded to the original payment method within 5–10 business days.",
        ],
      },
      {
        h: "7. Service Disruptions",
        p: "If a paid service is permanently and entirely undeliverable from our end (e.g., the program is officially shut down before any access has been delivered), affected students will be informed and offered an equivalent alternative at SkillPreneur Academy's discretion.",
      },
      {
        h: "8. Modification of Policy",
        p: "SkillPreneur Academy reserves the right to update or modify this Refund & Cancellation Policy at any time without prior notice. The latest version will always be published on this page.",
      },
      {
        h: "9. Contact for Refund-Related Queries",
        p: `For any refund-related questions (including duplicate-payment reviews), write to ${SUPPORT_EMAIL} with the subject line "Refund Query — <Order ID>". We respond within 24–48 business hours.`,
      },
    ],
  },
};

// Backward-compatible short slug aliases used by older links / consent labels
const ALIASES = {
  terms: "terms-and-conditions",
  privacy: "privacy-policy",
  refund: "refund-policy",
};

export default function LegalPage({ slug: slugProp }) {
  const params = useParams();
  const rawSlug = (slugProp || params.slug || "").toLowerCase();
  const slug = ALIASES[rawSlug] || rawSlug;
  const policy = POLICIES[slug];

  useEffect(() => {
    if (policy?.seoTitle) {
      const prev = document.title;
      document.title = policy.seoTitle;
      const meta =
        document.querySelector('meta[name="description"]') ||
        (() => {
          const m = document.createElement("meta");
          m.setAttribute("name", "description");
          document.head.appendChild(m);
          return m;
        })();
      const prevDesc = meta.getAttribute("content") || "";
      meta.setAttribute("content", policy.seoDescription || "");

      // canonical
      const canonHref = `${window.location.origin}${policy.canonical}`;
      let canon = document.querySelector('link[rel="canonical"]');
      const hadCanon = !!canon;
      const prevCanon = canon ? canon.getAttribute("href") : null;
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonHref);

      return () => {
        document.title = prev;
        meta.setAttribute("content", prevDesc);
        if (hadCanon) {
          if (prevCanon) canon.setAttribute("href", prevCanon);
        } else {
          canon.remove();
        }
      };
    }
  }, [policy]);

  if (!policy) {
    return (
      <main className="min-h-screen bg-white dark:bg-[#0B1220]">
        <Navigation />
        <div className="mx-auto max-w-3xl px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold text-[#0F172A] dark:text-white">
            Policy not found
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-[#2563EB] dark:text-[#06B6D4]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-white dark:bg-[#0B1220]"
      data-testid={`legal-${slug}`}
    >
      <Navigation />
      <article className="mx-auto max-w-3xl px-5 sm:px-6 py-14 sm:py-20">
        <Link
          to="/"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 dark:text-slate-400 hover:text-[#2563EB] dark:hover:text-[#06B6D4]"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Home
        </Link>

        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.04] px-3 py-1">
          <ShieldCheck className="h-3 w-3 text-[#06B6D4]" />
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500 dark:text-slate-400">
            SkillPreneur Academy · Legal
          </span>
        </div>

        <h1
          data-testid="legal-title"
          className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-[#0F172A] dark:text-white"
        >
          {policy.title}
        </h1>
        <p
          data-testid="legal-last-updated"
          className="mt-3 text-xs uppercase tracking-[0.22em] font-semibold text-slate-500 dark:text-slate-400"
        >
          Last updated · {LAST_UPDATED}
        </p>

        <p className="mt-6 text-base text-slate-600 dark:text-slate-300 leading-relaxed">
          {policy.intro}
        </p>

        {policy.callout && (
          <div
            data-testid="legal-callout"
            className="mt-6 rounded-2xl border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 px-5 py-4 text-[15px] font-medium text-amber-900 dark:text-amber-200 leading-relaxed"
          >
            {policy.callout}
          </div>
        )}

        <div className="mt-10 space-y-8">
          {policy.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-[#0F172A] dark:text-white">
                {s.h}
              </h2>
              <p className="mt-2 text-[15px] text-slate-600 dark:text-slate-300 leading-relaxed">
                {s.p}
              </p>
              {Array.isArray(s.list) && s.list.length > 0 && (
                <ul className="mt-3 space-y-2 text-[15px] text-slate-600 dark:text-slate-300 leading-relaxed list-disc pl-5 marker:text-[#06B6D4]">
                  {s.list.map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.04] p-5 text-sm text-slate-600 dark:text-slate-300">
          Questions about this policy? Email{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="legal-contact-email"
            className="text-[#2563EB] dark:text-[#06B6D4] font-semibold"
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          — we reply within 24 hours.
        </div>

        <div className="mt-8 flex flex-wrap gap-2 text-xs">
          <Link
            to="/terms-and-conditions"
            data-testid="legal-cross-terms"
            className="rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 hover:border-[#06B6D4]/40 hover:text-[#06B6D4]"
          >
            Terms & Conditions
          </Link>
          <Link
            to="/privacy-policy"
            data-testid="legal-cross-privacy"
            className="rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 hover:border-[#06B6D4]/40 hover:text-[#06B6D4]"
          >
            Privacy Policy
          </Link>
          <Link
            to="/refund-policy"
            data-testid="legal-cross-refund"
            className="rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 hover:border-[#06B6D4]/40 hover:text-[#06B6D4]"
          >
            Refund & Cancellation Policy
          </Link>
        </div>
      </article>
      <Footer />
    </main>
  );
}
